import React, { useEffect, useState, useMemo } from "react";
import { fetchApi } from "../../redux/api";
import { 
  Col, 
  Row, 
  Card, 
  Button, 
  ButtonGroup, 
  Form, 
  InputGroup,
  Badge,
  ListGroup 
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBell, 
  faCheckDouble, 
  faSearch, 
  faBellSlash 
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import NotificationItem from "../../components/NotificationItem";
import { 
  groupNotifications, 
  sortNotificationsByPriority, 
  filterNotifications 
} from "../../utils/notificationUtils";

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [groupingEnabled, setGroupingEnabled] = useState(true);
  const user = useSelector((state) => state.auth.user.username);

  useEffect(() => {
    setLoading(true);
    fetchApi(`notifications?type=fetchAllNotifications&mda_name=${user}`)
      .then((response) => {
        setNotifications(response.results || []);
      })
      .catch((error) => {
        console.error('Failed to fetch notifications:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user]);

  // Process notifications with filtering, searching, and grouping
  const processedNotifications = useMemo(() => {
    let filtered = notifications;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(notification =>
        notification.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.notification_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    switch (filter) {
      case 'unread':
        filtered = filterNotifications.unreadOnly(filtered);
        break;
      case 'urgent':
        filtered = filterNotifications.byPriority(filtered, 'URGENT');
        break;
      case 'important':
        filtered = filterNotifications.byPriority(filtered, 'IMPORTANT');
        break;
      case 'today':
        filtered = filterNotifications.byTimeRange(filtered, 24);
        break;
      default:
        // 'all' - no additional filtering
        break;
    }

    // Apply grouping if enabled
    if (groupingEnabled && filtered.length > 0) {
      filtered = groupNotifications(filtered);
    }

    // Sort by priority and time
    return sortNotificationsByPriority(filtered);
  }, [notifications, filter, searchTerm, groupingEnabled]);

  const handleNotificationAction = (actionType, notification) => {
    console.log(`Action ${actionType} on notification:`, notification);
    
    switch (actionType) {
      case 'APPROVE':
        // Handle quick approval
        console.log('Quick approve:', notification);
        markAsRead(notification.id);
        break;
        
      case 'DEFER':
        // Handle defer
        console.log('Defer notification:', notification);
        markAsRead(notification.id);
        break;
        
      case 'DISMISS':
        // Mark as read
        markAsRead(notification.id);
        break;
        
      default:
        console.log('Unknown action:', actionType);
    }
  };

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id
          ? { ...notification, status: "read" }
          : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, status: "read" }))
    );
  };

  const getFilterCounts = () => {
    return {
      all: notifications.length,
      unread: filterNotifications.unreadOnly(notifications).length,
      urgent: filterNotifications.byPriority(notifications, 'URGENT').length,
      important: filterNotifications.byPriority(notifications, 'IMPORTANT').length,
      today: filterNotifications.byTimeRange(notifications, 24).length,
    };
  };

  const filterCounts = getFilterCounts();

  if (loading) {
    return (
      <div className="container">
        <Card>
          <Card.Body className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading notifications...</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="container">
      <Card>
        <Card.Header className="bg-white border-bottom">
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-0 notification-title">
                <FontAwesomeIcon icon={faBell} className="text-primary" />
                <span>Notifications</span>{" "}
                <Badge bg="secondary" className="notification-count">
                  {notifications.length}
                </Badge>
              </h4>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={markAllAsRead}
                disabled={filterCounts.unread === 0}
              >
                <FontAwesomeIcon icon={faCheckDouble} className="me-1" />
                Mark All Read
              </Button>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body className="p-0">
          {/* Filters and Search */}
          <div className="p-3 border-bottom bg-light">
            <Row className="align-items-center">
              <Col md={8}>
                <ButtonGroup size="sm" className="notif-filter-group">
                  <Button
                    variant={filter === 'all' ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter('all')}
                  >
                    All {filterCounts.all > 0 && <Badge bg="light" text="dark" className="ms-1">{filterCounts.all}</Badge>}
                  </Button>
                  <Button
                    variant={filter === 'unread' ? 'primary' : 'outline-primary'}
                    onClick={() => setFilter('unread')}
                  >
                    Unread {filterCounts.unread > 0 && <Badge bg="danger" className="ms-1">{filterCounts.unread}</Badge>}
                  </Button>
                  <Button
                    variant={filter === 'urgent' ? 'danger' : 'outline-danger'}
                    onClick={() => setFilter('urgent')}
                  >
                    Urgent {filterCounts.urgent > 0 && <Badge bg="light" text="dark" className="ms-1">{filterCounts.urgent}</Badge>}
                  </Button>
                  <Button
                    variant={filter === 'important' ? 'warning' : 'outline-warning'}
                    onClick={() => setFilter('important')}
                  >
                    Important {filterCounts.important > 0 && <Badge bg="light" text="dark" className="ms-1">{filterCounts.important}</Badge>}
                  </Button>
                  <Button
                    variant={filter === 'today' ? 'info' : 'outline-info'}
                    onClick={() => setFilter('today')}
                  >
                    Today {filterCounts.today > 0 && <Badge bg="light" text="dark" className="ms-1">{filterCounts.today}</Badge>}
                  </Button>
                </ButtonGroup>
              </Col>
              
              <Col md={4} className="notif-filter-search">
                <InputGroup size="sm">
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faSearch} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search notifications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
            
            <Row className="mt-2">
              <Col>
                <Form.Check
                  type="switch"
                  id="grouping-switch"
                  label="Group similar notifications"
                  checked={groupingEnabled}
                  onChange={(e) => setGroupingEnabled(e.target.checked)}
                />
              </Col>
            </Row>
          </div>

          {/* Notifications List */}
          {processedNotifications.length > 0 ? (
            <ListGroup variant="flush">
              {processedNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onAction={handleNotificationAction}
                  onMarkAsRead={markAsRead}
                />
              ))}
            </ListGroup>
          ) : (
            <div className="text-center py-5">
              <FontAwesomeIcon 
                icon={faBellSlash} 
                size="3x" 
                className="text-muted mb-3" 
              />
              <h5 className="text-muted">No notifications found</h5>
              <p className="text-muted">
                {searchTerm || filter !== 'all' 
                  ? 'Try adjusting your filters or search terms'
                  : 'You\'re all caught up!'
                }
              </p>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Notifications;
